// Page de configuration du Widget Chat embeddable
// Génère le code d'intégration et permet la personnalisation

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Code, Copy, Check, Palette, Settings, Eye,
  ExternalLink, MessageSquare, Bot, Sparkles
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

const PRESET_COLORS = [
  { name: 'Bleu', value: '#3B82F6' },
  { name: 'Violet', value: '#8B5CF6' },
  { name: 'Vert', value: '#10B981' },
  { name: 'Orange', value: '#F97316' },
  { name: 'Rose', value: '#EC4899' },
  { name: 'Rouge', value: '#EF4444' },
  { name: 'Cyan', value: '#06B6D4' },
  { name: 'Jaune', value: '#EAB308' },
];

export default function WidgetPage() {
  const [config, setConfig] = useState(null);
  const [shopId, setShopId] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  
  // Widget customization
  const [widgetConfig, setWidgetConfig] = useState({
    color: '#3B82F6',
    position: 'right',
    greeting: '',
    bubbleText: 'Besoin d\'aide ?',
  });

  const token = localStorage.getItem('clientToken');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/api/vendor/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        setShopId(data.shop_id);
        if (data.custom_greeting) {
          setWidgetConfig(prev => ({ ...prev, greeting: data.custom_greeting }));
        }
      }
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const generateEmbedCode = () => {
    const baseUrl = window.location.origin;
    return `<script 
  src="${baseUrl}/widget/chat.js"
  data-shop-id="${shopId}"
  data-color="${widgetConfig.color}"
  data-position="${widgetConfig.position}"
  data-agent-name="${config?.agent_name || 'Assistant'}"
  ${widgetConfig.greeting ? `data-greeting="${widgetConfig.greeting.replace(/"/g, '&quot;')}"` : ''}
  ${widgetConfig.bubbleText ? `data-bubble-text="${widgetConfig.bubbleText.replace(/"/g, '&quot;')}"` : ''}
></script>`;
  };

  const copyCode = () => {
    navigator.clipboard.writeText(generateEmbedCode());
    setCopied(true);
    toast.success('Code copié !');
    setTimeout(() => setCopied(false), 2000);
  };

  const openPreview = () => {
    const previewUrl = `/widget-preview?shop=${shopId}&color=${encodeURIComponent(widgetConfig.color)}&position=${widgetConfig.position}&name=${encodeURIComponent(config?.agent_name || 'Assistant')}&greeting=${encodeURIComponent(widgetConfig.greeting)}&bubble=${encodeURIComponent(widgetConfig.bubbleText)}`;
    window.open(previewUrl, '_blank', 'width=500,height=700');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="widget-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <Code className="text-blue-600" size={28} />
          Widget Chat Embeddable
        </h1>
        <p className="text-gray-600 mt-1">
          Intègre ton agent IA sur n'importe quel site web
        </p>
      </div>

      {/* Preview Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye size={20} />
            Aperçu du Widget
          </CardTitle>
          <CardDescription>
            Voici comment ton widget apparaîtra sur ton site
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl p-8 min-h-[300px] relative">
            {/* Simulated website content */}
            <div className="bg-white rounded-lg p-6 shadow-sm mb-4">
              <div className="h-4 w-3/4 bg-gray-200 rounded mb-3"></div>
              <div className="h-4 w-1/2 bg-gray-200 rounded mb-3"></div>
              <div className="h-4 w-5/6 bg-gray-200 rounded"></div>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="h-4 w-2/3 bg-gray-200 rounded mb-3"></div>
              <div className="h-4 w-3/4 bg-gray-200 rounded"></div>
            </div>

            {/* Chat bubble preview */}
            <div 
              className={`absolute bottom-6 ${widgetConfig.position === 'right' ? 'right-6' : 'left-6'}`}
            >
              {widgetConfig.bubbleText && (
                <div 
                  className="bg-white rounded-lg px-4 py-3 shadow-lg mb-3 text-sm text-gray-700 max-w-[180px]"
                  style={{
                    marginLeft: widgetConfig.position === 'right' ? 'auto' : '0',
                    marginRight: widgetConfig.position === 'left' ? 'auto' : '0',
                  }}
                >
                  {widgetConfig.bubbleText}
                  <div 
                    className={`absolute -bottom-2 ${widgetConfig.position === 'right' ? 'right-6' : 'left-6'} w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-white`}
                  ></div>
                </div>
              )}
              <button
                className="w-14 h-14 rounded-full text-white shadow-lg flex items-center justify-center hover:scale-110 transition-transform"
                style={{ backgroundColor: widgetConfig.color }}
              >
                <MessageSquare size={24} />
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Customization */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Colors */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette size={20} />
              Couleur
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-4 gap-3">
              {PRESET_COLORS.map((preset) => (
                <button
                  key={preset.value}
                  onClick={() => setWidgetConfig({ ...widgetConfig, color: preset.value })}
                  className={`w-full aspect-square rounded-xl transition-transform hover:scale-105 ${
                    widgetConfig.color === preset.value ? 'ring-4 ring-offset-2 ring-blue-500' : ''
                  }`}
                  style={{ backgroundColor: preset.value }}
                  title={preset.name}
                />
              ))}
            </div>
            
            <div>
              <label className="block text-sm text-gray-600 mb-1">Couleur personnalisée</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={widgetConfig.color}
                  onChange={(e) => setWidgetConfig({ ...widgetConfig, color: e.target.value })}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <Input
                  value={widgetConfig.color}
                  onChange={(e) => setWidgetConfig({ ...widgetConfig, color: e.target.value })}
                  placeholder="#3B82F6"
                  className="flex-1"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Position & Text */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings size={20} />
              Paramètres
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Position</label>
              <div className="flex gap-3">
                <button
                  onClick={() => setWidgetConfig({ ...widgetConfig, position: 'left' })}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 transition-colors ${
                    widgetConfig.position === 'left' 
                      ? 'border-blue-500 bg-blue-50 text-blue-700' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  Gauche
                </button>
                <button
                  onClick={() => setWidgetConfig({ ...widgetConfig, position: 'right' })}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 transition-colors ${
                    widgetConfig.position === 'right' 
                      ? 'border-blue-500 bg-blue-50 text-blue-700' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  Droite
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Texte de la bulle d'accroche
              </label>
              <Input
                value={widgetConfig.bubbleText}
                onChange={(e) => setWidgetConfig({ ...widgetConfig, bubbleText: e.target.value })}
                placeholder="Besoin d'aide ?"
              />
              <p className="text-xs text-gray-500 mt-1">
                Apparaît au-dessus du bouton (laissez vide pour désactiver)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Message d'accueil personnalisé
              </label>
              <textarea
                value={widgetConfig.greeting}
                onChange={(e) => setWidgetConfig({ ...widgetConfig, greeting: e.target.value })}
                placeholder={`Bonjour ! Je suis ${config?.agent_name || 'ton assistant'}. Comment puis-je t'aider ?`}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Premier message affiché à l'ouverture du chat
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Embed Code */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-900">
            <Sparkles size={20} />
            Code d'intégration
          </CardTitle>
          <CardDescription className="text-blue-700">
            Copie ce code et colle-le avant la balise &lt;/body&gt; de ton site
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-gray-900 rounded-xl p-4 overflow-x-auto">
            <pre className="text-green-400 text-sm whitespace-pre-wrap font-mono">
              {generateEmbedCode()}
            </pre>
          </div>
          
          <div className="flex items-center gap-3 mt-4">
            <Button onClick={copyCode} className="bg-blue-600 hover:bg-blue-700">
              {copied ? (
                <>
                  <Check size={18} className="mr-2" />
                  Copié !
                </>
              ) : (
                <>
                  <Copy size={18} className="mr-2" />
                  Copier le code
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>Instructions d'installation</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-4">
            <li className="flex gap-4">
              <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold flex-shrink-0">1</span>
              <div>
                <p className="font-medium text-gray-900">Copiez le code d'intégration</p>
                <p className="text-gray-600 text-sm">Cliquez sur "Copier le code" ci-dessus</p>
              </div>
            </li>
            <li className="flex gap-4">
              <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold flex-shrink-0">2</span>
              <div>
                <p className="font-medium text-gray-900">Ouvre le code source de ton site</p>
                <p className="text-gray-600 text-sm">Accède au fichier HTML principal ou aux paramètres de ton CMS (WordPress, Wix, etc.)</p>
              </div>
            </li>
            <li className="flex gap-4">
              <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold flex-shrink-0">3</span>
              <div>
                <p className="font-medium text-gray-900">Collez le code avant &lt;/body&gt;</p>
                <p className="text-gray-600 text-sm">Le widget apparaîtra automatiquement sur toutes les pages</p>
              </div>
            </li>
            <li className="flex gap-4">
              <span className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center font-bold flex-shrink-0">✓</span>
              <div>
                <p className="font-medium text-gray-900">C'est terminé !</p>
                <p className="text-gray-600 text-sm">Ton agent IA est maintenant disponible pour tes visiteurs</p>
              </div>
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* CMS-specific instructions */}
      <Card>
        <CardHeader>
          <CardTitle>Guides par plateforme</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { name: 'WordPress', icon: '🔵' },
              { name: 'Wix', icon: '🟣' },
              { name: 'Shopify', icon: '🟢' },
              { name: 'Squarespace', icon: '⬛' },
            ].map((platform) => (
              <div 
                key={platform.name}
                className="p-4 border border-gray-200 rounded-xl text-center hover:border-blue-300 hover:bg-blue-50/50 transition-colors cursor-pointer"
              >
                <span className="text-2xl">{platform.icon}</span>
                <p className="font-medium text-gray-900 mt-2">{platform.name}</p>
                <p className="text-xs text-gray-500">Voir le guide</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
